/**
 * Sprint BG-DataSettings 30: ModeProfile を既存地区へ「上書き反映」する modal。
 *
 * Phase 1 scope (本 commit):
 *   - 地区一覧から 1 つ選択
 *   - その地区の現状 userFields / postFields のキー件数を「変更前」として表示
 *   - profile の userFields / postFields のキー件数を「変更後」として表示
 *   - 「全項目を上書き反映」ボタンで地区 dataSettings + userProfileOptions を置換
 *   - profileId / profileVersion を地区側に記録
 *
 * Phase 2 (未実装、別 sprint):
 *   - field 単位の選択的反映 (diff チェックリスト)
 *   - recommendedMasters の各 ID を District 各 field (analysisDisplayConfigId 等) に伝播
 */

import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import { type District, type FieldDef, type ModeProfile } from '@citizen-walk/shared-types';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useState } from 'react';
import { auth, db } from '../../lib/firebase';
import type { DistrictsState } from '../districts-admin';

export interface ModeProfileApplyModalProps {
  profile: ModeProfile;
  districtsState: DistrictsState;
  onClose: () => void;
}

function summarizeFields(fields: FieldDef[] | undefined): string {
  if (!fields || fields.length === 0) return '(空)';
  return fields.map((f) => f.key).join(', ');
}

export function ModeProfileApplyModal({
  profile,
  districtsState,
  onClose,
}: ModeProfileApplyModalProps) {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string>('');
  const [applying, setApplying] = useState(false);

  const districts = districtsState.status === 'ready' ? districtsState.items : [];
  const target: District | undefined = districts.find((d) => d.id === selectedId);

  async function handleApply() {
    if (!target) return;
    const confirmMsg =
      `「${target.name}」に profile「${profile.name}」(${profile.profileVersion}) を` +
      `上書き反映します。\n\n` +
      `現状の userFields / postFields は profile の内容で置換されます。よろしいですか?`;
    if (!confirm(confirmMsg)) return;

    setApplying(true);
    try {
      const ref = doc(db, 'districts', target.id);
      // reviewer follow-up ⚠️-2: 'admin-stub' 固定ではなく実 Firebase Auth uid を書く。
      // 未認証 (本来 admin app は AuthGate で守られているため通常ありえない) は 'admin-stub' fallback
      // で従来動作 (rules で reject される想定)。
      const uid = auth.currentUser?.uid ?? 'admin-stub';
      const payload: Record<string, unknown> = {
        dataSettings: {
          userFields: profile.userFields,
          postFields: profile.postFields,
        },
        profileId: profile.id,
        profileVersion: profile.profileVersion,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };
      if (profile.userProfileOptions) {
        payload.userProfileOptions = profile.userProfileOptions;
      }
      await setDoc(ref, payload, { merge: true });
      toast('success', `「${target.name}」に反映しました`);
      onClose();
    } catch (e) {
      toast('error', `反映失敗: ${(e as Error).message}`);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`profile「${profile.name}」を地区へ反映`}
      size="lg"
      closeOnOverlayClick={!applying}
    >
      <div className="grid grid-cols-1 gap-3">
        <p className="text-xs text-slate-500">
          反映先の地区を選択して、profile の userFields / postFields / userProfileOptions を上書き
          反映します (Phase 1: 全項目を一括上書き、field 単位の選択は別 sprint)。
        </p>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-600">反映先地区 (district、mode={profile.mode})</span>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">— 選択してください —</option>
            {districts
              .filter((d) => d.mode === profile.mode && !d.archivedAt)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.id})
                </option>
              ))}
          </select>
          <span className="text-[11px] text-slate-400">
            ※ 同じ mode の地区のみ表示しています (異 mode
            地区への適用は意図しない上書きを招くため非表示)
          </span>
        </label>

        {target && (
          <div className="border border-slate-200 rounded-md p-3 grid grid-cols-1 gap-2 bg-slate-50">
            <p className="text-xs font-bold text-slate-700">差分プレビュー</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="font-bold text-slate-600 mb-1">変更前 (現状の地区)</p>
                <p className="text-slate-700">
                  userFields ({target.dataSettings?.userFields?.length ?? 0} 件):{' '}
                  {summarizeFields(target.dataSettings?.userFields)}
                </p>
                <p className="text-slate-700 mt-1">
                  postFields ({target.dataSettings?.postFields?.length ?? 0} 件):{' '}
                  {summarizeFields(target.dataSettings?.postFields)}
                </p>
                <p className="text-slate-700 mt-1">
                  profileId: {target.profileId ?? '(未設定)'} / version:{' '}
                  {target.profileVersion ?? '(未設定)'}
                </p>
              </div>
              <div>
                <p className="font-bold text-slate-600 mb-1">変更後 (profile)</p>
                <p className="text-slate-700">
                  userFields ({profile.userFields.length} 件): {summarizeFields(profile.userFields)}
                </p>
                <p className="text-slate-700 mt-1">
                  postFields ({profile.postFields.length} 件): {summarizeFields(profile.postFields)}
                </p>
                <p className="text-slate-700 mt-1">
                  profileId: {profile.id} / version: {profile.profileVersion}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={onClose} disabled={applying}>
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleApply()}
            disabled={!target || applying}
          >
            {applying ? '反映中...' : '全項目を上書き反映'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
